'use strict';

/**
 * PictView-Connections
 * --------------------
 * The reusable drafting "connections" grid: an entity's links rendered as a tight grid of title-block
 * cells (count + mono label, with chip or thumbnail previews) instead of a tall stack of half-empty
 * sections. The same component the Blueprint uses, parameterized by colorway:
 *   - Colorway 'navy'     -> a curated Blueprint (the artifact).
 *   - Colorway 'graphite' -> an entity's auto-gathered connections (the everyday view).
 * Shared yellow accent (the link / pin gesture) on both.
 *
 * View mode hides empty relation types and previews media inline; click a cell to expand its items
 * underneath. Edit mode shows every possible relation as a dashed "+ link" cell plus a one-at-a-time
 * linker. Data + link/unlink flow through the ConnectionsAPI provider. configure(entity, id, colorway)
 * loads + paints into the destination.
 */

const libPictView = require('pict-view');

class PictViewConnections extends libPictView
{
	onBeforeInitialize()
	{
		this._data = { Title: '', Record: [], Groups: [], Available: [] };
		this._mode = 'view';
		this._active = null;
		this._linkType = null;
		this._linkItem = null;
		this._candidates = [];
		return super.onBeforeInitialize();
	}

	_api() { return this.pict.providers.ConnectionsAPI; }

	configure(pEntity, pID, pColorway, pHasPrompts)
	{
		this.options.Entity = pEntity || this.options.Entity;
		this.options.IDEntity = parseInt(pID, 10) || 0;
		if (pColorway) { this.options.Colorway = pColorway; }
		// Entities with attached prompts (WorkItem) host the prompts panel inside this box.
		this._hasPrompts = !!pHasPrompts;
		this._mode = 'view'; this._active = null; this._linkType = null; this._linkItem = null; this._candidates = [];
		return this.reload();
	}

	reload()
	{
		let tmpAPI = this._api();
		if (!tmpAPI || !this.options.IDEntity) { return Promise.resolve(); }
		return tmpAPI.get(this.options.Entity, this.options.IDEntity).then((pData) =>
		{
			this._data = pData || { Title: '', Record: [], Groups: [], Available: [] };
			this.render();
		});
	}

	// ---- shaping -------------------------------------------------------------------------------------
	// A rollup's Counts (server-ordered, e.g. Draft/Active/Realized/Abandoned) as bar segments +
	// a "2/5 realized" line, so a parent vision shows its sub-visions' aggregate progress right on
	// the cell -- no click-through needed (WI #6: Sub-vision status rollup).
	_rollupSlot(pGroup)
	{
		let tmpRollup = pGroup.Rollup;
		if (!tmpRollup || !tmpRollup.Total) { return []; }
		let tmpCounts = tmpRollup.Counts || [];
		return [{
			Segments: tmpCounts.map((pC) => ({
				Class: 'cnx-rollup-seg-' + String(pC.Status || '').toLowerCase(),
				Width: Math.round((pC.Count / tmpRollup.Total) * 100),
				Title: pC.Status + ': ' + pC.Count
			})),
			Text: tmpCounts.map((pC) => pC.Count + ' ' + String(pC.Status || '').toLowerCase()).join(' · ')
		}];
	}

	_cellRecord(pGroup, pEmpty)
	{
		let tmpItems = pGroup.Items || [];
		let tmpPreview = (pGroup.Mode === 'preview');
		return {
			Type: pGroup.Type, Label: pGroup.Label || pGroup.Type, Icon: pGroup.Icon || '', ViewHash: this.Hash,
			CountSlot: pEmpty ? [] : [{ Count: pGroup.Count }],
			PlusSlot: pEmpty ? [{ X: 1 }] : [],
			// Prefer the mark in the cell preview. A connections grid is scanned, not read, and three
			// marks (WI-301 F-167 V-043) cross-reference at a glance where three truncated sentences
			// cannot. The full titles are one click away in the detail rows.
			//
			// UNLESS the entity says otherwise. That argument holds for artifacts people cite BY mark and
			// inverts for one nobody does: a session carries SN-014 so it can be cited as provenance on a
			// printed sheet, and nobody ever says "open SN-014". Its descriptor sets ChipPrefersMark false
			// and the server passes the hint through, so the cell keeps the title it is scanned by.
			Chips: (!pEmpty && !tmpPreview) ? tmpItems.slice(0, 3).map((pIt) =>
				{
					let tmpUseMark = (pIt.ChipPrefersMark !== false) && !!pIt.Designator;
					return {
						Chip: (tmpUseMark ? pIt.Designator : '') || pIt.Chip || pIt.Title || '',
						MarkClass: tmpUseMark ? 'cnx-chip-mark' : ''
					};
				}) : [],
			Thumbs: (!pEmpty && tmpPreview) ? tmpItems.slice(0, 4).map((pIt) => ({ Thumb: pIt.Thumb || '', Title: pIt.Title || '' })) : [],
			RollupSlot: pEmpty ? [] : this._rollupSlot(pGroup),
			ActiveClass: (pGroup.Type === this._active) ? 'is-active' : '',
			EmptyClass: pEmpty ? 'is-empty' : '',
			WideClass: (!pEmpty && tmpPreview) ? 'is-wide' : ''
		};
	}

	_cells()
	{
		let tmpGroups = this._data.Groups || [];
		if (this._mode !== 'edit')
		{
			return tmpGroups.filter((pG) => pG.Count > 0).map((pG) => this._cellRecord(pG, false));
		}
		let tmpByType = {}; tmpGroups.forEach((pG) => { tmpByType[pG.Type] = pG; });
		return (this._data.Available || []).map((pA) =>
		{
			let tmpG = tmpByType[pA.Type];
			if (tmpG && tmpG.Count > 0) { return this._cellRecord(tmpG, false); }
			// Only offer the dashed "+ link" empty cell for relations you can actually link here.
			// Reverse relations, docs and media are managed elsewhere, so an empty one just hides.
			if (!pA.Linkable) { return null; }
			return this._cellRecord(Object.assign({ Count: 0, Mode: pA.Mode || 'count', Items: [] }, pA), true);
		}).filter((pC) => pC);
	}

	_detailSlot()
	{
		if (!this._active) { return []; }
		let tmpG = (this._data.Groups || []).find((pG) => pG.Type === this._active);
		if (!tmpG || !tmpG.Count) { return []; }
		let tmpEdit = (this._mode === 'edit');
		// Disconnect is only meaningful for a linkable relation (the server rejects the rest).
		let tmpAvail = (this._data.Available || []).find((pA) => pA.Type === this._active);
		let tmpCanUnlink = tmpEdit && !!(tmpAvail && tmpAvail.Linkable);
		return [{
			Label: tmpG.Label,
			Items: (tmpG.Items || []).map((pIt) => ({
				// A media item shows its thumbnail (clickable to the lightbox) in place of the generic type icon.
				IconHTML: pIt.Thumb ? '' : this.pict.providers.Icon.get(tmpG.Icon || ''), Title: pIt.Title || '', Type: tmpG.Type, ID: pIt.ID, ViewHash: this.Hash,
				ThumbSlot: pIt.Thumb ? [{ Thumb: pIt.Thumb, Title: pIt.Title || '' }] : [],
				ViewSlot: pIt.Thumb ? [{ Thumb: pIt.Thumb, Title: pIt.Title || '' }] : [],
				// A row for a linkable target opens it; everything else (media, comments) is plain text.
				// The mark leads the row where the item has one, so a detail list reads as cross-reference
				// (WI-301 Fold the executor) rather than a stack of sentences.
				MarkSlot: pIt.Designator ? [{ Designator: pIt.Designator }] : [],
				LinkTitleSlot: pIt.Link ? [{ Title: pIt.Title || '', Link: pIt.Link, ViewHash: this.Hash }] : [],
				PlainTitleSlot: pIt.Link ? [] : [{ Title: pIt.Title || '' }],
				// A rollup group's items carry the field it rolled up (Status, for sub-visions) --
				// show it as a badge on the row so the breakdown ties back to named parts.
				StatusSlot: (tmpG.Rollup && pIt.Status) ? [{ Status: pIt.Status, Class: 'cnx-rollup-seg-' + String(pIt.Status).toLowerCase() }] : [],
				UnlinkSlot: tmpCanUnlink ? [{ Type: tmpG.Type, ID: pIt.ID, ViewHash: this.Hash }] : []
			}))
		}];
	}

	// The relation types this entity can actually link one-at-a-time (fk / join with a target).
	// Everything else (reverse relations, docs, media, comments, sessions) is read-only here.
	_linkableTypes() { return (this._data.Available || []).filter((pA) => pA.Linkable); }

	_linkerSlot()
	{
		if (this._mode !== 'edit') { return []; }
		let tmpHasLinkable = this._linkableTypes().length > 0;
		return [{
			ViewHash: this.Hash,
			TypeHostId: this._typePickerHash(),
			CandHostId: this._candPickerHash(),
			// Show the two pickers when there is something to link; otherwise a plain note so the
			// editor never presents an empty "Choose a connection type" that can go nowhere.
			PickersSlot: tmpHasLinkable ? [{ ViewHash: this.Hash, TypeHostId: this._typePickerHash(), CandHostId: this._candPickerHash() }] : [],
			NoteSlot: tmpHasLinkable ? [] : [{ X: 1 }]
		}];
	}

	onBeforeRender(pRenderable)
	{
		let tmpColorway = (this.options.Colorway === 'navy') ? 'navy' : 'graphite';
		this.pict.AppData.Connections = {
			ViewHash: this.Hash,
			RootClass: 'cnx-' + tmpColorway,
			Eyebrow: this._data.Eyebrow || ((tmpColorway === 'navy') ? 'Blueprint' : 'Connections'),
			Title: this._data.Title || '',
			Record: this._data.Record || [],
			RecordWrap: (this._data.Record && this._data.Record.length) ? [{}] : [],
			Cells: this._cells(),
			ViewActive: (this._mode === 'view') ? 'is-on' : '',
			EditActive: (this._mode === 'edit') ? 'is-on' : '',
			LinkerSlot: this._linkerSlot(),
			DetailSlot: this._detailSlot(),
			PromptHostSlot: this._hasPrompts ? [{}] : []
		};
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		// Mount the attached-prompts panel into the host inside this box (the host is recreated on
		// every render, so re-mount each time). The panel fetches its own data.
		if (this._hasPrompts)
		{
			let tmpPrompts = this.pict.views['Plansheet-PromptAttachments'];
			if (tmpPrompts && typeof tmpPrompts.showFor === 'function') { tmpPrompts.showFor(this.options.Entity, this.options.IDEntity, 'Cnx-Prompts'); }
		}
		this._mountLinkerPickers();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	// ---- handlers ------------------------------------------------------------------------------------
	openCell(pType) { this._active = (this._active === pType) ? null : pType; this.render(); }

	// A connected item's route (fk / join / inverse-fk carry one; media and comments do not).
	openLink(pLink)
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp._navigate === 'function' && pLink) { tmpApp._navigate(pLink); }
	}

	setMode(pMode)
	{
		this._mode = (pMode === 'edit') ? 'edit' : 'view';
		if (this._mode !== 'edit') { this._linkType = null; this._linkItem = null; this._candidates = []; }
		this.render();
	}

	// ---- searchable pickers (type + candidate) ------------------------------------------------------
	// Hashes/value-addresses are namespaced by this view's Hash so two connection
	// boxes on a page (entity + blueprint) never share picker state.
	_typePickerHash() { return this.Hash + '-CnxTypePicker'; }
	_candPickerHash() { return this.Hash + '-CnxCandPicker'; }

	// One picker option per candidate: the title plus an enriched suffix (number +
	// status) so you can tell what you are connecting. The picker also surfaces the
	// full string as a hover tooltip.
	_candidateOptions()
	{
		return (this._candidates || []).map((pC) =>
		{
			let tmpMeta = [];
			if (pC.Number) { tmpMeta.push('#' + pC.Number); }
			if (pC.Status) { tmpMeta.push(pC.Status); }
			let tmpText = pC.Title || ('#' + pC.ID);
			if (tmpMeta.length) { tmpText += ' (' + tmpMeta.join(', ') + ')'; }
			return { Value: pC.ID, Text: tmpText };
		});
	}

	// Mount (or re-mount, after a render) the two pickers into their hosts. The
	// connections box replaces its DOM on every render, so re-create each time;
	// selections live in AppData (the picker ValueAddress), so they survive.
	_mountLinkerPickers()
	{
		if (this._mode !== 'edit') { return; }
		// Nothing linkable -> the template shows the note instead of picker hosts, so do not mount.
		if (this._linkableTypes().length === 0) { return; }
		let tmpPicker = this.pict.providers['Pict-Section-Picker'];
		if (!tmpPicker || typeof tmpPicker.createPicker !== 'function') { return; }

		let tmpTypeHash = this._typePickerHash();
		// Only offer types that can actually be linked; a non-linkable type would always come back
		// with "no options available" (the reported connections-editor bug).
		let tmpTypeOptions = this._linkableTypes().map((pA) => ({ Value: pA.Type, Text: pA.Label || pA.Type }));
		tmpPicker.createPicker(tmpTypeHash,
			{
				DestinationAddress: '#' + tmpTypeHash,
				ValueAddress: 'AppData.CnxLinker_' + this.Hash + '.Type',
				Placeholder: 'Choose a connection type',
				Searchable: (tmpTypeOptions.length > 8),
				Options: tmpTypeOptions,
				OnChange: (pVal) => this.onTypePicked(pVal)
			});
		let tmpTypeView = this.pict.views[tmpTypeHash];
		if (tmpTypeView) { tmpTypeView.setValue(this._linkType || ''); tmpTypeView.render(); }

		let tmpCandHash = this._candPickerHash();
		tmpPicker.createPicker(tmpCandHash,
			{
				DestinationAddress: '#' + tmpCandHash,
				ValueAddress: 'AppData.CnxLinker_' + this.Hash + '.Item',
				Placeholder: this._linkType ? 'Search what to connect' : 'Pick a type first',
				Searchable: true,
				Options: this._candidateOptions(),
				OnChange: (pVal) => this.onItemPicked(pVal)
			});
		let tmpCandView = this.pict.views[tmpCandHash];
		if (tmpCandView) { tmpCandView.setValue(this._linkItem || ''); tmpCandView.render(); }
	}

	// Type picked: load its candidates and refresh ONLY the candidate picker (no
	// full connections render, so the type selection stays put -- this is the
	// "doesn't stick" fix).
	onTypePicked(pType)
	{
		this._linkType = pType || null;
		this._linkItem = null;
		this._candidates = [];
		let tmpPicker = this.pict.providers['Pict-Section-Picker'];
		let tmpCandHash = this._candPickerHash();
		let fPaint = () =>
		{
			if (!tmpPicker) { return; }
			tmpPicker.createPicker(tmpCandHash,
				{
					Options: this._candidateOptions(),
					Placeholder: !this._linkType ? 'Pick a type first' : (this._candidates.length ? 'Search what to connect' : 'Nothing available to connect')
				});
			let tmpV = this.pict.views[tmpCandHash];
			if (tmpV) { tmpV.setValue(''); tmpV.render(); }
		};
		if (!this._linkType) { fPaint(); return; }
		this._api().candidates(this.options.Entity, this.options.IDEntity, this._linkType).then((pCands) =>
		{
			this._candidates = Array.isArray(pCands) ? pCands : [];
			fPaint();
		});
	}

	onItemPicked(pID) { this._linkItem = pID || null; }

	doLink()
	{
		if (!this._linkType || !this._linkItem) { return; }
		this._api().link(this.options.Entity, this.options.IDEntity, this._linkType, this._linkItem).then(() =>
		{
			this._active = this._linkType;
			this._linkType = null; this._linkItem = null; this._candidates = [];
			this.reload();
		});
	}

	unlinkItem(pType, pID)
	{
		this._api().unlink(this.options.Entity, this.options.IDEntity, pType, pID).then(() => this.reload());
	}
}

module.exports = PictViewConnections;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-Connections',
	DefaultRenderable: 'Connections-Display',
	DefaultDestinationAddress: '#Entity-Connections',
	AutoRender: false,
	Entity: '',
	IDEntity: 0,
	Colorway: 'graphite',
	CSSPriority: 520,
	CSS: /*css*/`
		.cnx { border-radius: 12px; padding: 16px 18px; border: 1.5px solid var(--cnx-frame); background: var(--cnx-sheet);
			/* Re-map the picker's theme tokens to the dark connections palette so the
			   pict-section-picker in the linker matches the box in any app theme. */
			--theme-color-background-primary: rgba(8,14,24,0.4);
			--theme-color-background-panel: var(--cnx-sheet);
			--theme-color-background-tertiary: rgba(150,166,188,0.22);
			--theme-color-text-primary: var(--cnx-txt);
			--theme-color-text-secondary: var(--cnx-lbl);
			--theme-color-text-muted: var(--cnx-lbl);
			--theme-color-border-default: var(--cnx-cellb);
			--theme-color-border-light: var(--cnx-cellb);
			--theme-color-border-strong: var(--cnx-frame);
			--theme-color-brand-primary: #f0c44a; }
		.cnx-graphite { --cnx-sheet: #262f3b; --cnx-eye: #9aabc0; --cnx-frame: rgba(214,224,236,0.42); --cnx-cellb: rgba(150,166,188,0.3); --cnx-lbl: #9aabc0; --cnx-txt: #eef2f7; }
		.cnx-navy { --cnx-sheet: #172c50; --cnx-eye: #84a6d4; --cnx-frame: rgba(208,226,247,0.5); --cnx-cellb: rgba(150,180,220,0.32); --cnx-lbl: #8fabd2; --cnx-txt: #eef4fc; }
		.cnx-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
		.cnx-eye { font-family: ui-monospace, Menlo, monospace; font-size: 11px; letter-spacing: 2px; color: var(--cnx-eye); text-transform: uppercase; }
		.cnx-title { font-size: 17px; font-weight: 500; color: var(--cnx-txt); margin-top: 3px; overflow-wrap: anywhere; }
		.cnx-modes { display: inline-flex; border: 1px solid var(--cnx-cellb); border-radius: 7px; overflow: hidden; flex: 0 0 auto; }
		.cnx-seg { font: inherit; font-size: 12px; padding: 4px 11px; border: none; background: transparent; color: var(--cnx-txt); cursor: pointer; }
		.cnx-seg.is-on { background: rgba(150,166,188,0.25); }
		.cnx-rec { display: flex; flex-wrap: wrap; border: 1px solid var(--cnx-cellb); border-radius: 8px; margin: 14px 0 16px; overflow: hidden; }
		.cnx-recrow { padding: 7px 13px; border-right: 1px solid var(--cnx-cellb); min-width: 0; }
		.cnx-recrow .cnx-eye { letter-spacing: 1.5px; }
		.cnx-recval { font-size: 14px; color: var(--cnx-txt); margin-top: 2px; display: block; }
		.cnx-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 10px; }
		.cnx-cell { min-width: 0; overflow: hidden; background: rgba(8,14,24,0.34); border: 1px solid var(--cnx-cellb); border-radius: 8px; padding: 11px 13px; cursor: pointer; text-align: left; font: inherit; }
		.cnx-cell:hover { border-color: rgba(210,224,245,0.55); }
		.cnx-cell.is-active { border-color: #f0c44a; }
		.cnx-cell.is-empty { border-style: dashed; }
		.cnx-cell.is-wide { grid-column: span 2; }
		.cnx-cell-top { display: flex; align-items: baseline; gap: 8px; flex-wrap: nowrap; }
		.cnx-n { font-size: 21px; font-weight: 500; color: var(--cnx-txt); }
		.cnx-plus { color: #f0c44a; font-size: 16px; }
		.cnx-cell-l { font-family: ui-monospace, Menlo, monospace; font-size: 11px; letter-spacing: 1.2px; color: var(--cnx-lbl); text-transform: uppercase; overflow-wrap: anywhere; flex: 1 1 auto; min-width: 0; }
		.cnx-chip { display: inline-block; max-width: 100%; background: rgba(140,165,200,0.2); color: var(--cnx-txt); font-size: 12px; padding: 2px 8px; border-radius: 5px; margin: 5px 5px 0 0; overflow-wrap: anywhere; }
		.cnx-thumb { display: inline-block; width: 32px; height: 32px; border-radius: 5px; overflow: hidden; border: 1px solid var(--cnx-cellb); margin: 6px 5px 0 0; background: rgba(140,165,200,0.16); vertical-align: top; cursor: zoom-in; }
		.cnx-thumb:hover { border-color: #f0c44a; }
		.cnx-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
		.cnx-detail-thumb { flex: 0 0 auto; width: 46px; height: 34px; border-radius: 4px; overflow: hidden; border: 1px solid var(--cnx-cellb); background: rgba(140,165,200,0.16); cursor: zoom-in; }
		.cnx-detail-thumb:hover { border-color: #f0c44a; }
		.cnx-detail-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
		.cnx-detail-view { margin-left: auto; flex: 0 0 auto; font: inherit; font-size: 11px; padding: 3px 10px; border: 1px solid var(--cnx-cellb); border-radius: 6px; background: transparent; color: var(--cnx-lbl); cursor: pointer; }
		.cnx-detail-view:hover { border-color: #f0c44a; color: #f0c44a; }
		.cnx-linker { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 12px; }
		.cnx-linker-note { font-size: 12px; color: var(--cnx-lbl); font-style: italic; }
		.cnx-pick { min-width: 168px; flex: 0 0 auto; }
		.cnx-pick-wide { flex: 1 1 220px; min-width: 200px; }
		.cnx-pick > .pict-section-picker, .cnx-pick > div { width: 100%; }
		.cnx-link-btn { font: inherit; font-size: 13px; padding: 7px 16px; border: 1.5px solid #f0c44a; color: #f0c44a; background: transparent; border-radius: 7px; cursor: pointer; flex: 0 0 auto; }
		.cnx-link-btn:hover { background: rgba(240,196,74,0.14); }
		.cnx-detail { background: rgba(8,14,24,0.34); border: 1px solid var(--cnx-cellb); border-radius: 8px; padding: 8px 14px; margin-top: 10px; }
		.cnx-detail-row { display: flex; align-items: flex-start; gap: 8px; padding: 6px 0; border-top: 1px solid var(--cnx-cellb); font-size: 13px; color: var(--cnx-txt); }
		.cnx-detail-row > .pict-icon { color: var(--cnx-eye); margin-top: 2px; flex: 0 0 auto; }
		.cnx-detail-mark { flex: 0 0 auto; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
			font-size: 11.5px; font-weight: 700; font-variant-numeric: tabular-nums; letter-spacing: 0.04em;
			color: #f0c44a; }
		.cnx-chip-mark { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
			font-variant-numeric: tabular-nums; letter-spacing: 0.03em; }
		/* Aggregate progress rollup (WI #6): a segmented bar + breakdown text on a cell like
		   "Parts of this vision", so a platform vision shows how its sub-visions are doing without
		   a click-through. Segment colors reuse the app's shared status palette. */
		.cnx-rollup { margin-top: 8px; }
		.cnx-rollup-bar { display: flex; height: 5px; border-radius: 3px; overflow: hidden; background: rgba(140,165,200,0.2); }
		.cnx-rollup-seg { display: block; height: 100%; }
		.cnx-rollup-seg-draft { background: var(--cnx-lbl, #9aabc0); }
		.cnx-rollup-seg-active { background: var(--theme-color-status-warning, #d0902f); }
		.cnx-rollup-seg-realized { background: var(--theme-color-status-success, #3fae5a); }
		.cnx-rollup-seg-abandoned { background: var(--theme-color-status-error, #c0353a); }
		.cnx-rollup-text { display: block; margin-top: 4px; font-size: 11px; color: var(--cnx-lbl); }
		.cnx-detail-status { flex: 0 0 auto; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; padding: 2px 8px; border-radius: 999px; color: #fff; }
		.cnx-detail-title { flex: 1; min-width: 0; overflow-wrap: anywhere; }
		a.cnx-detail-title { color: var(--cnx-txt); cursor: pointer; text-decoration: none; }
		a.cnx-detail-title:hover { color: #f0c44a; text-decoration: underline; }
		.cnx-unlink { margin-left: auto; flex: 0 0 auto; cursor: pointer; font: inherit; font-size: 11px; letter-spacing: 0.3px; padding: 3px 9px; border: 1px solid var(--cnx-cellb); border-radius: 6px; background: transparent; color: var(--cnx-lbl); display: inline-flex; align-items: center; gap: 5px; }
		.cnx-unlink:hover { border-color: #f0c44a; color: #f0c44a; }
		.cnx-prompts { margin-top: 16px; padding-top: 14px; border-top: 1px solid var(--cnx-cellb); }
		.cnx-prompts > .cnx-eye { display: block; margin-bottom: 10px; }
	`,
	Templates:
	[
		{
			Hash: 'Connections-Display',
			Template: /*html*/`
<div class="cnx {~D:AppData.Connections.RootClass~}">
	<div class="cnx-head">
		<div style="min-width:0;"><div class="cnx-eye">{~D:AppData.Connections.Eyebrow~}</div><div class="cnx-title">{~D:AppData.Connections.Title~}</div></div>
		<div class="cnx-modes">
			<button class="cnx-seg {~D:AppData.Connections.ViewActive~}" onclick="_Pict.views['{~D:AppData.Connections.ViewHash~}'].setMode('view')">View</button>
			<button class="cnx-seg {~D:AppData.Connections.EditActive~}" onclick="_Pict.views['{~D:AppData.Connections.ViewHash~}'].setMode('edit')">Edit</button>
		</div>
	</div>
	{~TS:Connections-RecordWrap:AppData.Connections.RecordWrap~}
	<div class="cnx-grid">{~TS:Connections-Cell:AppData.Connections.Cells~}</div>
	{~TS:Connections-Linker:AppData.Connections.LinkerSlot~}
	{~TS:Connections-Detail:AppData.Connections.DetailSlot~}
	{~TS:Connections-PromptHost:AppData.Connections.PromptHostSlot~}
</div>`
		},
		{ Hash: 'Connections-PromptHost', Template: /*html*/`<div class="cnx-prompts"><div class="cnx-eye">Prompts</div><div id="Cnx-Prompts"></div></div>` },
		{
			Hash: 'Connections-Record',
			Template: /*html*/`<div class="cnx-recrow"><div class="cnx-eye">{~D:Record.Label~}</div><span class="cnx-recval">{~D:Record.Value~}</span></div>`
		},
		{
			Hash: 'Connections-RecordWrap',
			Template: /*html*/`<div class="cnx-rec">{~TS:Connections-Record:AppData.Connections.Record~}</div>`
		},
		{
			Hash: 'Connections-Cell',
			Template: /*html*/`<button class="cnx-cell {~D:Record.ActiveClass~} {~D:Record.EmptyClass~} {~D:Record.WideClass~}" onclick="_Pict.views['{~D:Record.ViewHash~}'].openCell('{~D:Record.Type~}')"><span class="cnx-cell-top">{~TS:Connections-Count:Record.CountSlot~}{~TS:Connections-Plus:Record.PlusSlot~}<span class="cnx-cell-l">{~D:Record.Label~}</span></span><span class="cnx-chiprow">{~TS:Connections-Chip:Record.Chips~}</span><span class="cnx-thumbrow">{~TS:Connections-Thumb:Record.Thumbs~}</span>{~TS:Connections-Rollup:Record.RollupSlot~}</button>`
		},
		{ Hash: 'Connections-Count', Template: /*html*/`<span class="cnx-n">{~D:Record.Count~}</span>` },
		{ Hash: 'Connections-Plus', Template: /*html*/`<span class="cnx-n cnx-plus">+</span>` },
		{ Hash: 'Connections-Chip', Template: /*html*/`<span class="cnx-chip {~D:Record.MarkClass~}">{~D:Record.Chip~}</span>` },
		{
			Hash: 'Connections-Rollup',
			Template: /*html*/`<span class="cnx-rollup"><span class="cnx-rollup-bar">{~TS:Connections-RollupSeg:Record.Segments~}</span><span class="cnx-rollup-text">{~D:Record.Text~}</span></span>`
		},
		{ Hash: 'Connections-RollupSeg', Template: /*html*/`<span class="cnx-rollup-seg {~D:Record.Class~}" style="width:{~D:Record.Width~}%" title="{~D:Record.Title~}"></span>` },
		{ Hash: 'Connections-Thumb', Template: /*html*/`<span class="cnx-thumb" data-url="{~D:Record.Thumb~}" data-title="{~D:Record.Title~}" title="Click to view" onclick="event.stopPropagation();if(this.dataset.url){_Pict.PictApplication.viewImage(this.dataset.url,this.dataset.title);}"><img src="{~D:Record.Thumb~}" alt="" loading="lazy"></span>` },
		{
			Hash: 'Connections-Linker',
			Template: /*html*/`<div class="cnx-linker"><span class="cnx-eye">Link</span>{~TS:Connections-LinkerPickers:Record.PickersSlot~}{~TS:Connections-LinkerNote:Record.NoteSlot~}</div>`
		},
		{
			Hash: 'Connections-LinkerPickers',
			Template: /*html*/`<div class="cnx-pick" id="{~D:Record.TypeHostId~}"></div><div class="cnx-pick cnx-pick-wide" id="{~D:Record.CandHostId~}"></div><button class="cnx-link-btn" onclick="_Pict.views['{~D:Record.ViewHash~}'].doLink()">Link it</button>`
		},
		{ Hash: 'Connections-LinkerNote', Template: /*html*/`<span class="cnx-linker-note">Nothing here can be linked directly.</span>` },
		{
			Hash: 'Connections-Detail',
			Template: /*html*/`<div class="cnx-detail"><span class="cnx-eye">{~D:Record.Label~}</span>{~TS:Connections-Detail-Row:Record.Items~}</div>`
		},
		{
			Hash: 'Connections-Detail-Row',
			Template: /*html*/`<div class="cnx-detail-row">{~TS:Connections-DetailThumb:Record.ThumbSlot~}{~D:Record.IconHTML~}{~TS:Connections-DetailMark:Record.MarkSlot~}{~TS:Connections-DetailTitleLink:Record.LinkTitleSlot~}{~TS:Connections-DetailTitlePlain:Record.PlainTitleSlot~}{~TS:Connections-DetailStatus:Record.StatusSlot~}{~TS:Connections-DetailView:Record.ViewSlot~}{~TS:Connections-Unlink:Record.UnlinkSlot~}</div>`
		},
		{ Hash: 'Connections-DetailThumb', Template: /*html*/`<span class="cnx-detail-thumb" data-url="{~D:Record.Thumb~}" data-title="{~D:Record.Title~}" title="Click to view" onclick="_Pict.PictApplication.viewImage(this.dataset.url,this.dataset.title)"><img src="{~D:Record.Thumb~}" alt="" loading="lazy"></span>` },
		{ Hash: 'Connections-DetailMark', Template: /*html*/`<span class="cnx-detail-mark">{~D:Record.Designator~}</span>` },
		{ Hash: 'Connections-DetailStatus', Template: /*html*/`<span class="cnx-detail-status {~D:Record.Class~}">{~D:Record.Status~}</span>` },
		{ Hash: 'Connections-DetailTitleLink', Template: /*html*/`<a class="cnx-detail-title" onclick="_Pict.views['{~D:Record.ViewHash~}'].openLink('{~D:Record.Link~}')">{~D:Record.Title~}</a>` },
		{ Hash: 'Connections-DetailTitlePlain', Template: /*html*/`<span class="cnx-detail-title">{~D:Record.Title~}</span>` },
		{ Hash: 'Connections-DetailView', Template: /*html*/`<button type="button" class="cnx-detail-view" data-url="{~D:Record.Thumb~}" data-title="{~D:Record.Title~}" onclick="_Pict.PictApplication.viewImage(this.dataset.url,this.dataset.title)">View</button>` },
		{ Hash: 'Connections-Unlink', Template: /*html*/`<button class="cnx-unlink" onclick="_Pict.views['{~D:Record.ViewHash~}'].unlinkItem('{~D:Record.Type~}','{~D:Record.ID~}')" title="Disconnect">{~I:Unlink~} Disconnect</button>` }
	],
	Renderables:
	[
		{ RenderableHash: 'Connections-Display', TemplateHash: 'Connections-Display', ContentDestinationAddress: '#Entity-Connections', RenderMethod: 'replace' }
	]
};
